import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Command } from "cmdk";
import {
  CornerDownLeft,
  FilePlus2,
  Layers,
  Package,
  Plus,
  Search,
  Truck,
  Wallet,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { commandRoutes } from "@/config/navigation";

type CommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const itemClass =
  "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs text-muted-foreground aria-selected:bg-accent aria-selected:text-accent-foreground";

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  function go(to: string) {
    void navigate({ to });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-md">
        <DialogTitle className="sr-only">Jump to page</DialogTitle>
        <Command
          loop
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:text-muted-foreground/70"
        >
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search className="size-3.5 shrink-0 text-muted-foreground" />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Jump, create, or search… (Ctrl+K)"
              className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
            />
          </div>
          <Command.List className="max-h-80 overflow-y-auto p-1.5">
            <Command.Empty className="py-8 text-center text-xs text-muted-foreground">
              Nothing matched “{query}”.
            </Command.Empty>

            <Command.Group heading="Create">
              <Command.Item
                value="new purchase order po create"
                onSelect={() => go("/purchase/orders/new")}
                className={itemClass}
              >
                <FilePlus2 className="size-3.5" />
                <span className="flex-1 text-foreground">New purchase order</span>
              </Command.Item>
              <Command.Item
                value="new client create customer"
                onSelect={() => go("/clients/new")}
                className={itemClass}
              >
                <Plus className="size-3.5" />
                <span className="flex-1 text-foreground">New client</span>
              </Command.Item>
              <Command.Item
                value="new vendor create supplier"
                onSelect={() => go("/vendors/new")}
                className={itemClass}
              >
                <Truck className="size-3.5" />
                <span className="flex-1 text-foreground">New vendor</span>
              </Command.Item>
              <Command.Item
                value="new material create catalogue"
                onSelect={() => go("/materials/new")}
                className={itemClass}
              >
                <Layers className="size-3.5" />
                <span className="flex-1 text-foreground">New material</span>
              </Command.Item>
              <Command.Item
                value="new expense add cost"
                onSelect={() => go("/expenses")}
                className={itemClass}
              >
                <Wallet className="size-3.5" />
                <span className="flex-1 text-foreground">Go to expenses</span>
              </Command.Item>
              <Command.Item
                value="purchase orders list receive stock"
                onSelect={() => go("/purchase")}
                className={itemClass}
              >
                <Package className="size-3.5" />
                <span className="flex-1 text-foreground">Purchase orders</span>
              </Command.Item>
            </Command.Group>

            <Command.Group heading="Navigation">
              {commandRoutes.map((route) => (
                <Command.Item
                  key={route.to}
                  value={`${route.group} ${route.title}`}
                  onSelect={() => go(route.to)}
                  className={itemClass}
                >
                  <CornerDownLeft className="size-3 opacity-0 aria-selected:opacity-100" />
                  <span className="flex-1 text-foreground">{route.title}</span>
                  <span className="text-2xs">{route.group}</span>
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
